import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: inline-flex;
            align-items: center;
            padding-left: 0;
            margin-right: 25px;
            border-left: 1px solid #ccc;
            border-right: 1px solid #ccc;
            line-height: 1.3;
            position: relative;
            vertical-align: middle;

            .lang-text {
                display: inline-block;
                vertical-align: middle;
            }

            .lang-img {
                height: 14px;
                width: 14px;
                margin: -2px 5px 0 0;
                position: relative;
                top: 2px;
            }

            &:focus:not(:focus-visible) {
                outline: none;
            }
            &:focus-visible {
                outline: 2px dashed #2060ad;
            }

            div[class^="FormInputWrapper"],
            div[class*=" FormInputWrapper"] {
                width: 72px;
                &:focus-visible {
                    background: none;
                    border: none;
                    outline: none !important;

                    select[data-test-selector="currencyMenu"] {
                        background: none;
                        border: none;
                    }
                }
            }

            .dropdown-header {
                background: none;
                border: none;
                color: #535e64;
                font-family: "Roboto",sans-serif;
                font-size: .875rem;
                padding-right: 15px;
                padding-left: 15px;
                height: auto;
                position: relative;
                font-weight: bold;
                cursor: pointer;

                &:focus:not(:focus-visible) {
                    outline: none;
                }
                &:focus-visible {
                    outline: 2px dashed #2060ad;
                }

                .arrows {
                    margin-right: 0;
                    margin-left: 5px;
                    width: 14px;
                    height: 14px;
                    display: inline-flex;
                    align-items: center;
                    vertical-align: middle;

                    svg {
                        color: #535e64;
                        font-size: .875rem;
                        width: 14px;
                        height: 14px;
                    }
                }

                .up-arrow {
                    display: none;
                }

                &.active {
                    .up-arrow {
                        display: inline-block;
                        vertical-align: middle;
                        width: 14px;
                        height: 14px;

                        svg {
                            color: #535e64;
                            font-size: .875rem;
                        }
                    }
                    .down-arrow {
                        display: none;
                    }
                }
            }

            .dropdown-list {
                position: absolute;
                left: 0;
                bottom: auto;
                top: 100%;
                z-index: 99;
                padding: 12px;
                background: #fff;
                border: 1px solid #c3d4ea;
                width: 215px;
                max-height: 335px;
                overflow-y: auto;
                box-shadow: rgba(0,37,84,0.15);

                .dropdown-item {
                    cursor: pointer;
                    padding: 17px 23px;
                    border-top: 1px solid #c2d1d9;
                    color: #2e384d;
                    letter-spacing: 0.17px;
                    font-family: 'Lato', sans-serif;

                    &:focus:not(:focus-visible) {
                        outline: none;
                    }
                    &:focus-visible {
                        outline: 2px dashed #2060ad;
                    }

                    &:first-child {
                        border-top: none;
                    }

                    &.current {
                        color: #2e384d;
                        background: #f3f8fe;
                        font-weight: bold;
                        text-decoration: underline;
                    }

                    &:hover {
                        color: #2e384d;
                        background: #f3f8fe;
                        font-weight: bold;
                        text-decoration: underline;
                    }
                }
            }
        `,
    },
    languageIcon: {
        size: 14,
        src: Globe,
        css: css`
            margin-top: -2px;
            margin-right: 5px;
            color: #535e64;
            font-size: .875rem;
            display: inline-block;
            vertical-align: middle;
        `,
    },
    languageSelect: {
        backgroundColor: "common.accent",
        cssOverrides: {
            formInputWrapper: css`
                width: 135px;
            `,
            inputSelect: css`
                border: none;
                text-transform: uppercase;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

export const LogoImage = styled.img`
    height: 22px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({ languages, currentLanguage, fields, setLanguage }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                toggleLanguageMenuHide();
            }
        };

        window.addEventListener("click", handleClickOutside);

        return () => {
            window.removeEventListener("click", handleClickOutside);
        };
    }, []);
      

    if (!languages || languages.length <= 1) {
        return null;
    }

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    const toggleLanguageMenu = () => {
        setIsOpen(true);
    };

    const toggleLanguageMenuHide = () => {
        setIsOpen(false);
    };

    const handleLanguageChange = (languageId: string) => {
        setLanguage({ languageId });
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.keyCode === 27) {
            e.stopPropagation();
            setIsOpen(false);
        }
        if (e.keyCode === 13) {
            e.stopPropagation();
            setIsOpen(!isOpen);
        }
    };

    if ((typeof window !== "undefined") && (window as any).showLanguageIcon === undefined) {
        (window as any).showLanguageIcon = fields.showIcon;
    }
    
    return (
        <StyledWrapper {...styles.languageWrapper} onClick={toggleLanguageMenu}  
        onKeyDown={(e) => handleKeyDown(e)}
        tabIndex={0}
        ref={wrapperRef}>
            <Typography as="div">
                <Typography as="div" className={`dropdown-header ${isOpen ? 'active' : ' '}`}>
                    {fields.showIcon &&
                    (currentLanguage?.imageFilePath ? (
                        <LogoImage className="lang-img" src={currentLanguage.imageFilePath} alt="" />
                    ) : (
                        <Icon {...styles.languageIcon} />
                    ))}
                    <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                        {translate("Language")}
                    </VisuallyHidden>
                    <Typography as="span" className="lang-text">{currentLanguage?.description}</Typography>
                    <Icon className="arrows down-arrow" src="ChevronDown" />
                    <Icon className="arrows up-arrow" src="ChevronUp" />
                </Typography>
                {isOpen && (
                    <Typography as="div" className="dropdown-list">
                        {languages.map((c) => (
                            <Typography 
                                as="div" 
                                onClick={() => handleLanguageChange(c.id)}
                                key={c.id}
                                className={`dropdown-item ${c.description === currentLanguage?.description ? 'current' : ' '}`}
                                tabIndex={0}
                            >
                                {c.description}
                            </Typography>
                        ))}
                    </Typography>
                )}
            </Typography>
        </StyledWrapper>
    );
};
          


const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
